// src/context/PlaceContext.js
//
// THE PAGE YOU ARE ON IS FROGGERAI'S CURRENT PLACE (I-03 R6 · S3-06.3,
// proposal §3e; operator ruling 2026-09-10: "the dock is on every
// facility page; the page IS the current place; it navigates").
//
// "Feed tank two" said standing at rack 3 means rack 3's tank 2. The
// dock cannot know that from the utterance — the PAGE knows it, so the
// pages say so: the racks page sets the room in view, RackDetail (and
// the phone rack sheet) the rack, the tank drawer the tank, the animal
// drawer the animal. The dock reads one value and sends its ids on every
// turn.
//
// ── WHY A STACK AND NOT ONE SLOT ──────────────────────────────────────
//
// Several setters are mounted at once and their effects fire in an order
// nobody controls: React runs a CHILD's effect before its parent's, so
// RackDetail's "I am rack 3" lands BEFORE RacksBoard's "I am the main
// room", and a last-writer-wins slot would say "the main room" while the
// operator is looking at rack 3. Worse, whichever setter unmounted last
// would clear a place another mounted setter still owns.
//
// So every setter holds its own ENTRY, keyed, and the effective place is
// the MOST SPECIFIC entry — room < rack < enclosure < animal = clutch — with the
// most recent push breaking a tie. That answer does not depend on effect
// order at all, and an entry is removed only by the setter that added
// it. Closing a tank drawer therefore falls back to its rack rather than
// to nothing, which is what the operator standing in front of the rack
// means by "here".
//
// ── THE CHIP SHOWS WHAT THE SERVER HOLDS (FABLE_QUEUE "RULINGS 2026-10-30
// evening", Q-chip-nowhere-is-sent; the leftovers batch, item 10) ──────
//
// The server keeps the last room / rack / tank it was sent until it is
// told otherwise — "no place sends NO field" exists so a page that is not
// a place does not wipe the rack set two taps ago. The SCREEN used to go
// blank the moment the rack page unmounted, while the server still held
// the rack: the keeper read "Nowhere in particular" and the next spoken
// feeding landed at the rack anyway. So the provider keeps the LAST room /
// rack / tank (an animal as its tank) as a STICKY, lowest-rank place, shown
// whenever no mounted page says otherwise, until a page sets a new place
// or the keeper taps ×. The × sends `{}` ("nowhere") on the next turn and
// on every turn until a new place is set, so screen and server forget
// together. A CLUTCH is never sticky on either side (R3): the server drops
// it on any turn that does not send it, so collapsing its row drops it
// from the chip and the request alike — the clutch's TANK, which the
// server does keep, is what stays.
//
// Vocabulary: room / rack / enclosure (tank) / animal / clutch. Never the other
// word for an enclosure.
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';

import { placeIsKnown } from '../services/api';
import { useSignedInUserId } from './AuthContext';
import { froggerKeys } from '../utils/froggerStorageKeys';

/** The kinds a place may be, LEAST specific first. The order IS the rule. */
export const PLACE_KINDS = ['room', 'rack', 'enclosure', 'animal', 'clutch'];

/**
 * How specific each kind is. A CLUTCH ranks WITH an animal (slice 2a,
 * FABLE_QUEUE "Q-spoken-hatch-reaches-editClutch", item 7): both are a
 * subject in a tank, and neither is more specific than the other — so,
 * as for any two places of equal rank, the later-set one wins.
 */
const RANK = {
  room: 0, rack: 1, enclosure: 2, animal: 3, clutch: 3,
};

export const PlaceContext = createContext(null);

/**
 * A stable identity for a place, so "the same place set twice" is a
 * no-op and "cleared" can survive a re-render without surviving a
 * navigation. Kind AND id, because a rack and a tank can share neither.
 */
export const placeKey = (place) => (
  place && place.kind && place.id ? `${place.kind}:${place.id}` : null);

/** True when two places are the same place said twice. */
export const samePlace = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;
  return placeKey(a) === placeKey(b)
    && (a.name || '') === (b.name || '')
    && placeKey(a.parent || null) === placeKey(b.parent || null);
};

/** The kinds the SERVER keeps from one turn to the next. */
const STICKY_KINDS = ['room', 'rack', 'enclosure'];

/**
 * What the server holds after a turn sent ``place`` — the sticky place —
 * given what it held before (``prev``). Mirrors `placeToStreamIds` and the
 * server's `apply_place`:
 *   room / rack / tank          -> itself
 *   an animal in a known tank   -> that tank (an animal goes as its tank)
 *   an animal in no known tank  -> unchanged (nothing is sent for it)
 *   a clutch in a known tank    -> that tank (the clutch is turn-scoped)
 *   a clutch in no known tank   -> nothing (its `{group_id}` replaced the
 *                                  server's place, then dropped)
 */
export const stickyAfter = (place, prev = null) => {
  if (!place || !placeKey(place)) return prev;
  if (STICKY_KINDS.includes(place.kind)) return place;
  const parent = place.parent || null;
  const tank = (parent && parent.kind === 'enclosure' && parent.id) ? parent : null;
  if (place.kind === 'animal') return tank || prev;
  if (place.kind === 'clutch') return tank;
  return prev;
};

/**
 * THE STICKY PLACE OUTLIVES A RELOAD, AS THE SERVER'S DOES (item 10). The
 * conversation id lives in `sessionStorage`, so after a reload the server
 * still holds the rack; a chip that forgot it on reload would be the
 * defect this item exists for. It is kept per TAB (sessionStorage) and per
 * FACILITY — the first uuid in the path (`/facilities/<id>/…`,
 * `/my-store/<id>/…`) — so another facility's rack is never shown (or
 * sent) on this one's pages. A path with no uuid says nothing about scope.
 *
 * AND PER USER (R-TRAINING junior Cowork fix, 2026-11-04): the key is
 * `froggerKeys.stickyPlace(<signed-in user>)`, and a change of user
 * re-reads it — an Owner's "Rack 2" never shows on the junior who signs
 * in after them in the same tab.
 */
const UUID_IN_PATH = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

export const placeScope = () => {
  try {
    const match = String(window.location.pathname || '').match(UUID_IN_PATH);
    return match ? match[0].toLowerCase() : '';
  } catch (e) {
    return '';
  }
};

const readStickyStore = (userId) => {
  try {
    const raw = sessionStorage.getItem(froggerKeys.stickyPlace(userId));
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      scope: String(parsed.scope || ''),
      place: parsed.place && placeKey(parsed.place) ? parsed.place : null,
      nowhere: Boolean(parsed.nowhere),
    };
  } catch (e) {
    return null;
  }
};

/** The most specific entry, ties broken by the most recent push. */
export const bestOf = (entries) => {
  let best = null;
  entries.forEach((entry) => {
    if (!entry || !entry.place) return;
    const rank = RANK[entry.place.kind];
    if (rank === undefined) return;
    if (!best || rank > best.rank || (rank === best.rank && entry.seq > best.seq)) {
      best = { rank, seq: entry.seq, place: entry.place };
    }
  });
  return best ? best.place : null;
};

export function PlaceProvider({ children }) {
  // Whose sticky place this is (the signed-in user; null when nobody is).
  const userId = useSignedInUserId();
  // `entries` is a Map key -> {place, seq}; state is a plain object so a
  // re-render is a new reference and memo below recomputes honestly.
  const [entries, setEntries] = useState({});
  // What the operator has DISMISSED by tapping the chip. It is a KEY,
  // not a boolean: dismissing "rack:3" must not also dismiss the tank
  // they walk to next, and it must not come back when rack 3's own
  // component re-renders.
  const [dismissed, setDismissed] = useState(null);
  // The STICKY place (item 10): what the server holds when no mounted
  // page says anything. Lowest rank — shown only when there is no entry.
  const storedRef = useRef(undefined);
  if (storedRef.current === undefined) storedRef.current = readStickyStore(userId);
  const [sticky, setSticky] = useState(
    () => (storedRef.current ? storedRef.current.place : null));
  // The facility the sticky place belongs to (`placeScope` when it was set).
  const [stickyScope, setStickyScope] = useState(
    () => (storedRef.current ? storedRef.current.scope : ''));
  // The keeper tapped ×: "nowhere" until a page sets a new place. The
  // wire then carries `{}` (`placeForWire`) so the server forgets too.
  const [nowhere, setNowhere] = useState(
    () => Boolean(storedRef.current && storedRef.current.nowhere));
  const seqRef = useRef(0);
  // Already-asked lookups (declared here: the user switch below resets it).
  const checkedRef = useRef({});

  // A DIFFERENT USER IS A DIFFERENT STICKY PLACE. The provider is mounted
  // once for the app, so a sign-out and a sign-in as somebody else keep
  // this instance — and with it the last user's place in memory. Reset
  // DURING RENDER (React's derived-state pattern), so no effect ever sees
  // the old user's place beside the new user's id and writes it under the
  // new user's key.
  const [owner, setOwner] = useState(userId);
  if (owner !== userId) {
    const stored = readStickyStore(userId);
    setOwner(userId);
    setSticky(stored ? stored.place : null);
    setStickyScope(stored ? stored.scope : '');
    setNowhere(Boolean(stored && stored.nowhere));
    setDismissed(null);
    checkedRef.current = {};
  }

  const pushPlace = useCallback((key, place) => {
    if (!key) return;
    seqRef.current += 1;
    const seq = seqRef.current;
    setEntries((prev) => {
      const existing = prev[key];
      // IDEMPOTENT: setting the same place twice keeps the same entry
      // (and the same seq), so a parent re-render cannot reorder the
      // stack under a setter that said nothing new.
      if (existing && samePlace(existing.place, place)) return prev;
      return { ...prev, [key]: { place, seq } };
    });
  }, []);

  const dropPlace = useCallback((key) => {
    if (!key) return;
    setEntries((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  /**
   * Imperative set — the escape hatch, not the usual route. It holds one
   * entry of its own so a caller cannot evict a mounted page's place;
   * `setPlace(null)` releases it.
   */
  const setPlace = useCallback((place) => {
    setDismissed(null);
    if (place && placeKey(place)) setNowhere(false);
    if (!place || !placeKey(place)) {
      dropPlace('imperative');
      return;
    }
    pushPlace('imperative', place);
  }, [pushPlace, dropPlace]);

  const raw = useMemo(() => bestOf(Object.values(entries)), [entries]);
  const rawKey = placeKey(raw);
  const shownRaw = (raw && rawKey !== dismissed) ? raw : null;

  // A page that SAYS a place (and is not the one the keeper dismissed)
  // moves the sticky place and ends "nowhere". Read by VALUE through the
  // key, the name and the parent, so a re-render that says nothing new
  // moves nothing.
  const shownKey = placeKey(shownRaw);
  const shownName = shownRaw ? (shownRaw.name || '') : '';
  const shownParent = placeKey(shownRaw && shownRaw.parent);
  useEffect(() => {
    if (!shownRaw) return;
    setSticky((prev) => {
      const next = stickyAfter(shownRaw, prev);
      return samePlace(next, prev) ? prev : next;
    });
    setStickyScope(placeScope());
    setNowhere(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownKey, shownName, shownParent]);

  /**
   * DISMISSAL IS PER PLACE, not a mode. Tapping the chip clears the
   * place the keeper is looking at; walking to the next rack shows a
   * chip again without anything having to un-dismiss. A page that is
   * still mounted keeps its entry, so nothing has to be re-derived when
   * the operator changes their mind by navigating.
   */
  const clearPlace = useCallback(() => {
    setDismissed(placeKey(raw));
    setSticky(null);
    setStickyScope(placeScope());
    setNowhere(true);
    dropPlace('imperative');
  }, [raw, dropPlace]);

  // Kept for the next reload, per tab.
  useEffect(() => {
    try {
      sessionStorage.setItem(froggerKeys.stickyPlace(owner), JSON.stringify(
        { scope: stickyScope, place: sticky, nowhere }));
    } catch (e) { /* private mode: in memory only */ }
  }, [sticky, stickyScope, nowhere, owner]);

  // Another facility's sticky place (or its "nowhere") is not this one's.
  const here = placeScope();
  const sameScope = !here || !stickyScope || here === stickyScope;
  const place = shownRaw || (nowhere || !sameScope ? null : sticky);

  // ── THE STICKY CHIP FORGETS A PLACE THAT IS GONE (FABLE_QUEUE "Filed by
  // the Opus driver 2026-11-02 ~00:30"; the legacy park batch, item 6) ──
  //
  // After the operator's beta `cleanup_qadelete`, the chip still read
  // "QADELETE leftover t…" — a tank that no longer exists, kept in
  // sessionStorage — and would have sent its id. The sticky place, when it
  // is what the chip SHOWS (no mounted page says a place), is looked up
  // ONCE per place on this facility (`placeIsKnown`); a 404 drops it: the
  // chip reads "Nowhere in particular" and the next turn sends `{}` (the ×'s
  // own "nowhere", so the server forgets with the screen). Unknown (a room,
  // a 403, no network) keeps it — only a definite "not this facility's"
  // drops a place. DECISION: no lookup with no facility in the path.
  const stickyRef = useRef(sticky);
  stickyRef.current = sticky;
  const stickyShownKey = (!shownRaw && !nowhere && sameScope && sticky)
    ? placeKey(sticky) : null;
  useEffect(() => {
    if (!stickyShownKey || !here || typeof placeIsKnown !== 'function') return;
    const checkKey = `${here}|${stickyShownKey}`;
    if (checkedRef.current[checkKey]) return;
    checkedRef.current[checkKey] = true;
    const asked = stickyRef.current;
    Promise.resolve(placeIsKnown(here, asked)).then((known) => {
      if (known !== false) return;
      if (placeKey(stickyRef.current) !== stickyShownKey) return;
      setSticky(null);
      setNowhere(true);
    }).catch(() => { /* unknown: keep the place */ });
  }, [stickyShownKey, here]);

  const value = useMemo(() => ({
    place, nowhere: !place && nowhere && sameScope, setPlace, clearPlace, pushPlace, dropPlace,
  }), [place, nowhere, sameScope, setPlace, clearPlace, pushPlace, dropPlace]);

  return (
    <PlaceContext.Provider value={value}>{children}</PlaceContext.Provider>
  );
}

/**
 * The dock's read. Safe outside a provider (returns an inert value) so a
 * component can be unit-tested without one.
 */
export function usePlace() {
  return useContext(PlaceContext) || {
    place: null,
    nowhere: false,
    setPlace: () => {},
    clearPlace: () => {},
    pushPlace: () => {},
    dropPlace: () => {},
  };
}

/**
 * THE SETTER PAGES USE. Declare the place this component IS; it is set
 * while mounted and removed on unmount — and only ever ITS entry.
 *
 * `place` may be null ("this component is not a place right now"), which
 * is how a tank drawer says it has closed without touching the rack's
 * entry.
 */
export function usePlaceSetter(place) {
  const ctx = usePlace();
  const { pushPlace, dropPlace } = ctx;
  // One key per MOUNT, not per place: a component that walks from rack 1
  // to rack 3 owns one entry the whole time. `useRef` with a lazy init
  // so the id is stable across renders.
  const keyRef = useRef(null);
  if (keyRef.current === null) {
    keyRef.current = `setter:${Math.random().toString(36).slice(2)}`;
  }
  // The place is compared by VALUE, so a page rebuilding the same object
  // literal on every render does not churn the provider.
  const key = placeKey(place);
  const name = place ? (place.name || '') : '';
  const parentKey = placeKey(place && place.parent);

  useEffect(() => {
    if (!key) {
      dropPlace(keyRef.current);
      return undefined;
    }
    pushPlace(keyRef.current, place);
    return undefined;
    // `place` is deliberately absent: its VALUE is the three primitives
    // below, and depending on the object would push on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, name, parentKey, pushPlace, dropPlace]);

  // Unmount is its OWN effect with no deps, so a place change never runs
  // the teardown (which would drop the entry and re-add it, moving it to
  // the top of the tie-break for no reason).
  useEffect(() => {
    const owned = keyRef.current;
    return () => dropPlace(owned);
  }, [dropPlace]);
}

/** "Rack 3" — the whole label, for a title or a sentence. */
export const placeLabel = (place) => {
  if (!place) return '';
  const name = (place.name || '').trim();
  if (!name) return place.kind === 'enclosure' ? 'this tank' : `this ${place.kind}`;
  // A rack whose name already SAYS rack takes no prefix — the "does the
  // name CONTAIN the word" rule (see `placeChipParts`), not "does it start
  // with it": "Breeding rack" read "At Rack Breeding rack" (item 15).
  if (place.kind === 'rack' && !/\brack\b/i.test(name)) return `Rack ${name}`;
  if (place.kind === 'room' && !/\broom\b/i.test(name)) return `${name} room`;
  // A clutch whose name already says "clutch" carries no noun (the rack
  // rule): "QADELETEVoiceDam clutch 2026-10-29", never "Clutch QADELETE…
  // clutch …".
  if (place.kind === 'clutch' && !/\bclutch\b/i.test(name)) return `Clutch ${name}`;
  return name;
};

/** The noun a keeper uses for each kind. An animal is called by its name. */
const KIND_NOUN = {
  rack: 'Rack', enclosure: 'Tank', room: 'Room', animal: '', clutch: 'Clutch',
};

/**
 * THE CHIP'S THREE PARTS — `{kind, name, context}` — batch 8 item B,
 * grown a third part by the phone eye test of 2026-09-12 (below).
 *
 * Operator, phone eye test 2026-09-12: *"AT … way too small — if that's
 * saying what rack we're at, I can't see it; it's a big deal."* Two
 * things were wrong with one string. It was tiny (that is the CSS, see
 * `FroggerDock.css`), and it spent its first four characters on the word
 * `At:` and then repeated the kind — "At: Rack Rack 3" — so the part
 * that says WHICH rack was the part that got ellipsized away.
 *
 * So the chip is built from two pieces instead of one sentence: the
 * NAME, which is what the keeper is looking for and is rendered bold,
 * and the KIND, which is context and is rendered muted — and is DROPPED
 * ENTIRELY when the name already carries it.
 *
 *   {kind: 'rack',      name: 'Rack 3'}  -> {kind: '',     name: 'Rack 3'}
 *   {kind: 'rack',      name: '3'}       -> {kind: 'Rack', name: '3'}
 *   {kind: 'room',      name: 'Frog Room'} -> {kind: '',   name: 'Frog Room'}
 *   {kind: 'enclosure', name: '1A'}      -> {kind: 'Tank', name: '1A'}
 *
 * ⚠️ THE TEST IS "DOES THE NAME CONTAIN THE WORD", NOT "DOES IT START
 * WITH IT". A room called "Frog Room" carries its noun at the END, and
 * stripping a LEADING word would have left "Room Frog Room" — the
 * duplication this exists to remove, in a new place.
 *
 * ── AND THE THIRD PART: WHERE THAT NAME IS ───────────────────────────
 *
 * Operator, phone pass on beta 2026-09-13: the chip said "At:…" and
 * nothing else, because the name had been squeezed to an ellipsis. The
 * size half of that is the CSS (the chip now gets a ROW of its own on a
 * phone); this half is that "1A" on its own does not say where 1A IS.
 *
 * `context` is the PARENT's label — the same `placeLabel` the aria
 * sentence uses — so a tank reads "1A · Rack 3" and an animal
 * "Kiwi · 1A". It is rendered MUTED, after the name, and the name is
 * still the bold half that survives everything.
 *
 *   {kind:'enclosure', name:'1A', parent: rack 3} -> context 'Rack 3'
 *   {kind:'animal',    name:'Kiwi', parent: 1A}   -> context '1A'
 *   a rack, a room, or anything with no parent    -> context ''
 *
 * ⚠️ A PARENT WITH NO NAME CONTRIBUTES NOTHING. `placeLabel` answers
 * "this rack" for a nameless one, which is a placeholder, and a chip
 * reading "1A · this rack" tells the keeper less than "1A" alone. Today
 * only tanks and animals carry a `parent` at all (`RacksBoard`,
 * `PhoneRackDetail`, `AnimalsGrid`), which is why racks and rooms come
 * out empty without a special case for them.
 */
export const placeChipParts = (place) => {
  if (!place) return { kind: '', name: '', context: '' };
  const name = (place.name || '').trim()
    || (place.kind === 'enclosure' ? 'this tank' : `this ${place.kind}`);
  const parent = place.parent || null;
  const context = (parent && (parent.name || '').trim()) ? placeLabel(parent) : '';
  const noun = KIND_NOUN[place.kind] || '';
  if (!noun) return { kind: '', name, context };
  const carried = new RegExp(`\\b${noun}\\b`, 'i').test(name);
  return { kind: carried ? '' : noun, name, context };
};

/**
 * The ids the stream body carries (`langgraph/views._parse_place`).
 *
 * THE BACKEND KNOWS FOUR KINDS — rack, enclosure, room and (slice 2a)
 * clutch — and resolves every id against the facility before it believes
 * a word of it. An ANIMAL is not one of them, so an animal place is sent
 * as the TANK it is in when the drawer knows it (`parent`), and as
 * nothing at all when it does not. It is never invented: a made-up
 * enclosure id would be sticky in the session and would be handed to
 * tools as a locus.
 *
 * A CLUTCH is sent as its own `group_id` AND its tank's `enclosure_id`
 * (its `parent`, when the row knows it) — ids only; the NAMES are read
 * off the rows on the server. No place still sends nothing.
 */
export const placeToStreamIds = (place) => {
  if (!place) return null;
  const out = {};
  const add = (p) => {
    if (!p || !p.id) return;
    if (p.kind === 'rack') out.rack_id = String(p.id);
    else if (p.kind === 'enclosure') out.enclosure_id = String(p.id);
    else if (p.kind === 'room') out.room_id = String(p.id);
    else if (p.kind === 'clutch') out.group_id = String(p.id);
  };
  add(place);
  if (place.kind === 'animal' || place.kind === 'clutch') add(place.parent);
  else if (place.kind === 'enclosure' && place.parent) add(place.parent);
  return Object.keys(out).length ? out : null;
};

/**
 * What the request carries for this turn (item 10): the place's ids
 * (`placeToStreamIds`, unchanged — no place sends NO field), or `{}` —
 * "nowhere" — once the keeper has tapped × and no page has set a place
 * since, so the server forgets with the screen.
 */
export const placeForWire = (place, nowhere = false) => {
  if (place) return placeToStreamIds(place);
  return nowhere ? {} : null;
};

export default PlaceProvider;
